import { Box, type GridItemProps } from "@chakra-ui/react";
import { horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { TaskRenderColumnSimple } from "@src/components/modules/resource-planning/tasks/listing/builders";
import { TaskRowItem } from "@src/components/modules/resource-planning/tasks/listing/components/row-item";
import { observer } from "mobx-react-lite";
import { Resizable } from "re-resizable";
import type { CSSProperties } from "react";

interface TaskHeaderItemProps extends Omit<GridItemProps, "sx"> {
  onColumnResize?: (newWidth: string) => void;
  onResizeStop?: () => void;
  onResizeStart?: (columnKey: string) => void;
  isReorderable?: boolean;
  column: TaskRenderColumnSimple;
  sx?: CSSProperties;
  handleZIndex?: number;
}
export const TaskHeaderItem = observer(function TaskHeaderItem({
  onResizeStart,
  onResizeStop,
  sx,
  onColumnResize,
  isReorderable,
  column,
  handleZIndex,
  ...props
}: TaskHeaderItemProps) {
  const isResizeable = Boolean(
    onResizeStart !== undefined &&
      onColumnResize !== undefined &&
      onResizeStop !== undefined &&
      column !== undefined,
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column?.key ?? "",
    disabled: !isReorderable,
    strategy: horizontalListSortingStrategy,
    data: {
      key: column.key,
      width: column.width,
      title: column.title,
    },
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Resizable
      enable={{ right: isResizeable, left: false, top: false, bottom: false }}
      size={{
        width: column.width,
      }}
      onResizeStart={() => {
        onResizeStart?.(column.key);
      }}
      onResize={(_, _direction, ref) => {
        onColumnResize?.(ref.style.width);
      }}
      minWidth="55px"
      onResizeStop={onResizeStop}
      style={sx}
      handleStyles={{
        right: {
          right: "0",
          top: "0",
          bottom: "0",
          width: "4px",
          zIndex: handleZIndex,
        },
      }}
    >
      <TaskRowItem
        {...props}
        {...attributes}
        ref={setNodeRef}
        sx={{ ...style, ...sx }}
        pos="relative"
        role="group"
        isReorderable={isReorderable}
        listeners={listeners}
        noOfLines={1}
      >
        {isDragging ? <Box h="20px" /> : props.children}
      </TaskRowItem>
    </Resizable>
  );
});
